// @ts-nocheck
import { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { Action } from './Action';
import { signupForPlan } from '../lib/appUrls';

// Mirrors platform-core/scripts/seedstatio/main.go. Changing a price here without
// changing the seed ships marketing the billing system will not honour.
/**
 * One axis, six slots, same order in every tier.
 *
 * These were four independent feature lists of different lengths in different
 * orders, Free led with servers and ended on retention, Business ended on SIEM
 * export, and "Per-tool access policies" appeared only on Team. Comparing what
 * an upgrade actually buys you meant reading four columns and holding them in
 * your head, which is recall, not recognition. The 18-row matrix on /pricing
 * already answered this; the landing page did not.
 *
 * Now every card renders the same six rows in the same order, so a reader scans
 * ACROSS one row, servers, then hosted, then calls, instead of down four
 * lists. A slot a tier does not include says so rather than being omitted,
 * because a missing row silently shifts every row beneath it out of alignment
 * and destroys the axis that is the entire point.
 *
 * The order is the order the questions get asked: how many servers, how many
 * can you host for me, how much traffic, how many of us, what can I control,
 * how long do you keep the record.
 */
export const SLOTS = ['Servers', 'Hosted', 'API calls', 'Members', 'Policies', 'Audit logs'];

export const tiers = [
  {
    name: 'Free',
    price: '$0',
    period: '',
    description: 'Evaluate Statio. No credit card required, hard limits, never a surprise bill.',
    features: [
      '3 MCP servers',
      '1 from your spec, sleeps when idle',
      '10,000 / month',
      '1 member',
      'Per-tool allow and deny',
      '90 days',
    ],
    cta: 'Start free',
    highlight: false,
  },
  {
    name: 'Developer',
    price: '$19',
    period: '/mo',
    description: 'For a solo developer running agents in production.',
    features: [
      '10 MCP servers',
      '3 from your spec',
      '50,000 / month',
      '1 member',
      'Per-tool allow and deny',
      '90 days',
    ],
    cta: 'Start on Developer',
    highlight: false,
  },
  {
    name: 'Team',
    price: '$149',
    period: '/mo',
    description: 'For teams sharing one governed endpoint.',
    features: [
      '25 MCP servers',
      '10 from your spec or image',
      '500,000 / month',
      'Up to 15 members',
      'Full RBAC: roles, teams, grants',
      '90 days',
    ],
    cta: 'Start on Team',
    highlight: true,
  },
  {
    name: 'Business',
    price: '$599',
    period: '/mo',
    description: 'For organizations that have to answer to a security review.',
    features: [
      'Unlimited',
      '50 from your spec or image',
      '2,000,000 / month',
      'Unlimited members',
      'Full RBAC + SSO/SAML',
      '90 days, export and SIEM',
    ],
    cta: 'Start on Business',
    highlight: false,
  },
];
export function Pricing() {
  return (
    <section className="l-pricing" id="pricing" aria-labelledby="pricing-title">
      <h2 id="pricing-title">Flat monthly prices. Never per seat.</h2>
      <p className="sub">
        Inside a plan, adding a member never changes your bill. Free needs no card and has no path to a charge.
      </p>
      <div className="l-tiers">
        {tiers.map((tier) => (
          <article key={tier.name} className={tier.highlight ? 'l-tier hi' : 'l-tier'}>
            <h3>{tier.name}</h3>
            <div className="price">
              {tier.price}
              <small>{tier.period}</small>
            </div>
            <p className="desc">{tier.description}</p>
            <ul>
              {tier.features.map((f, slot) => (
                <li key={SLOTS[slot]}>
                  <span className="k">{SLOTS[slot]}</span>
                  {f}
                </li>
              ))}
            </ul>
            <Action
              href={signupForPlan(tier.name)}
              external
              className={tier.highlight ? 's-btn s-btn-pri' : 's-btn s-btn-sec'}
            >
              {tier.cta}
            </Action>
          </article>
        ))}
        <article className="l-tier">
          <h3>Enterprise</h3>
          <div className="price">
            from $15K<small>/yr</small>
          </div>
          <p className="desc">Self-hosted in your cloud.</p>
          <ul>
            <li>Unlimited servers and members</li>
            <li>Custom call volume, records kept up to two years</li>
            <li>99.9% SLA, named contact</li>
            <li>Audit exports for your review</li>
          </ul>
          <Action href="mailto:dans@romans.dev?subject=Statio%20Enterprise" className="s-btn s-btn-sec">
            Talk to us
          </Action>
        </article>
      </div>
      <div className="l-fineprint">
        <p>
          <b>Annual billing.</b> Pay for 10 months, get 12. Available on every paid tier.
        </p>
        <p>
          <b>Extra hosted servers.</b> $9/month each, beyond the number your tier includes.
        </p>
        <p>
          <b>Overage is off.</b> Go past your monthly API calls and the extra calls are refused, at no charge, until you
          turn overage on yourself. If you do, you set a monthly spend cap first, and the calls past your allowance bill
          at $30 per 100,000, the same rate Team and Business include.
        </p>
      </div>
      <div className="l-faq">
        <h2>Common questions</h2>
        {FAQ.map((faq, index) => (
          <FAQItem key={faq.q} id={String(index)} question={faq.q} answer={faq.a} />
        ))}
      </div>
    </section>
  );
}

const FAQ = [
  {
    q: 'Why not just run an MCP gateway myself?',
    a: 'You can. Routing is the easy part. What takes the time is everything around it: per-tool permissions checked on every call, credentials encrypted at rest and never handed to the agent, an audit trail your security team accepts, and discovering the servers developers installed without telling anyone. Statio is that layer, and it works without an EDR or MDM, which is where most self-built setups stop.',
  },
  {
    q: 'How is this different from an enterprise AI governance platform?',
    a: 'Mostly that you can start right now. Ours is self-serve with published pricing: sign up, connect a server, set a policy, no demo call and no annual contract. The enterprise platforms in this space gate everything behind sales, which works if you are 500 people and does not if you are twelve.',
  },
  {
    q: 'What happens when I hit my call limit?',
    a: 'On Free, calls beyond 10,000/month are refused and nothing is charged. There is no way to spend money on the Free plan. On a paid plan you can turn on overage billing at $30 per additional 100,000 calls. It is off until you enable it, and you set a monthly spend cap when you do.',
  },
  {
    q: 'What counts as a hosted MCP server?',
    a: 'One that runs on our infrastructure, and there are two ways to get one. Give us an OpenAPI spec and we generate the server, build it and run it, on every plan including Free. Or, from Team up, give us a Docker image of a server you already built and we run that image as it is. Hosted servers sleep when idle. Servers you install from the catalog run on shared Statio infrastructure and do not count, and external MCPs you host yourself and connect by URL do not count either. Extra hosted servers are $9/month each.',
  },
  {
    q: 'Are members billed per seat?',
    a: 'No. Every plan is a flat monthly price. Adding someone to your org never changes your bill. Developer is a single-member plan, Team covers up to 15 members, and Business and Enterprise are unlimited.',
  },
  {
    q: 'Can I switch plans or cancel anytime?',
    a: 'Yes. Upgrade, downgrade, or cancel from your dashboard. Cancelling drops you to Free at the end of the period rather than deleting anything. Your servers and audit history stay put.',
  },
  {
    q: 'How long are audit logs retained?',
    a: 'Every plan keeps 90 days of audit log, 30 days of tool calls and 13 months of model calls, and you can shorten the first two in settings. Business adds file exports and SIEM streaming. Enterprise keeps records for up to two years.',
  },
];

/**
 * A disclosure row.
 *
 * This was a YStack -- a plain div -- with an onPress handler, no role, no
 * tabindex and no aria-expanded, so all seven rows were unreachable by keyboard
 * and announced as static text. The trigger is now a real <button> wrapped in an
 * <h3>, which is the pattern that gives a screen reader both the outline entry
 * and the expand/collapse state.
 *
 * The answer is ALWAYS rendered (closed: .x-collapse, visibility hidden). It used
 * to be `{open && ...}`, so none of the seven answers existed in the prerendered
 * HTML -- verified against dist/index.html, where the question "gateway myself"
 * appeared once and its answer zero times. These are the page's best long-tail
 * search copy and none of it was indexable.
 */
export function FAQItem({ question, answer, id }: { question: string; answer: string; id: string }) {
  const [open, setOpen] = useState(false);
  const answerId = `faq-answer-${id}`;
  return (
    <div className="l-faq-item">
      <h3>
        <button
          type="button"
          className="l-faq-q"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={answerId}
        >
          {question}
          <span className="s-caret" aria-hidden="true">
            <CaretDown size={18} weight="bold" />
          </span>
        </button>
      </h3>
      {/* Rendered while closed so the answers stay in the prerendered HTML; .x-collapse slides it open and hides it from assistive tech while closed. */}
      <div id={answerId} className="x-collapse" data-open={open || undefined}>
        <div>
          <p className="l-faq-a">{answer}</p>
        </div>
      </div>
    </div>
  );
}
